// Single source of truth for charterService fallback content.
// Used by BOTH:
//   - Astro SSR (src/lib/jsonld.js resolveEntity) — falls back here
//     when Sanity returns no matching charterService doc, so detail
//     pages always have SSR-renderable content.
//   - React client (src/components/App.jsx CHARTER_SERVICES map) —
//     same fallback consumed at render time when __CMS.CHARTER_SERVICES
//     is empty for a slug.
//
// Shape matches Sanity GROQ projection (src/lib/sanity.js charterServices
// query) so consumers don't branch on data source — title, subtitle,
// hero (URL string), intro, aircraft[], highlights[], popularRoutes[],
// features[]. Image URLs are plain strings (not IMG.* references) so
// the module is server-safe — no DOM globals, no React imports.

export const CHARTER_SERVICES_FALLBACK = {
  "charter-island": {
    title: "Island Transfers",
    subtitle: "The fastest way between islands",
    hero: "https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&q=80&auto=format&fit=crop",
    color: "#0D56A6",
    aircraft: ["r66","h125","aw109","h145"],
    intro: "Island-hopping by helicopter redefines the Greek summer. Instead of planning your entire trip around ferry schedules -- often booking weeks in advance to guarantee a spot on a fast catamaran -- you simply book your aircraft and go. The flight from Athens to Mykonos takes 28 minutes. Athens to Santorini, 42 minutes. Mykonos to Santorini, 20 minutes. Connections that take half a day by sea become a single drink of water and a view you will remember for years.",
    highlights: [
      { icon: "stopwatch", label: "Time saved", desc: "28 min Athens-Mykonos vs 4h by ferry" },
      { icon: "lock", label: "Fully private", desc: "Whole aircraft for your group only" },
      { icon: "luggage", label: "Luggage", desc: "Up to 10 bags depending on aircraft" },
      { icon: "mappin", label: "Flexible routing", desc: "Multi-island itineraries available" },
    ],
    popularRoutes: ["athens-mykonos","athens-santorini","mykonos-santorini","athens-paros","athens-ios"],
  },
  "charter-yacht": {
    title: "Yacht Transfers",
    subtitle: "Sea-to-shore and superyacht coordination",
    hero: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=800&q=80&auto=format&fit=crop",
    color: "#0D56A6",
    aircraft: ["aw109","h145"],
    intro: "For superyacht guests and owners, helicopter transfers have become the natural complement to time at sea. Whether you need to move guests from Athens airport directly to a yacht at anchor in the Saronic Gulf, transfer between a vessel and a Mykonos villa for a dinner reservation, or arrange a scenic arrival for guests joining the yacht mid-voyage, our team coordinates every detail with your captain and crew.",
    highlights: [
      { icon: "anchor", label: "Superyacht coordination", desc: "Direct liaison with your captain and crew" },
      { icon: "sun", label: "Sea landing capable", desc: "Platform and beach landings arranged" },
      { icon: "zap", label: "Last-minute available", desc: "Subject to aircraft availability" },
      { icon: "globe", label: "Any waypoint", desc: "We fly to any approved landing zone" },
    ],
    popularRoutes: ["athens-mykonos","athens-porto-heli","mykonos-santorini"],
  },
  "charter-sightseeing": {
    title: "Sightseeing Flights",
    subtitle: "The Caldera, the Acropolis, Meteora -- from above",
    hero: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=1800&q=85&auto=format&fit=crop",
    color: "#0D56A6",
    aircraft: ["r66","h125","aw109","h145"],
    intro: "Some things are simply different from altitude. The Santorini caldera, seen from a helicopter at 500 metres, is one of the most dramatic sights in the Mediterranean -- a ring of volcanic cliffs, bleached white villages, and deep indigo water. The Acropolis of Athens, with the city spreading in every direction, gains a completely new perspective from the air. Meteora, rising impossibly from the Thessalian plain, looks even more otherworldly from above.",
    highlights: [
      { icon: "camera", label: "Aerial photography", desc: "Large windows, low altitude on request" },
      { icon: "hourglass", label: "30-90 min flights", desc: "Tailored to your interests" },
      { icon: "gift", label: "Special occasions", desc: "Birthdays, anniversaries, proposals" },
      { icon: "compass", label: "Custom routes", desc: "Tell us what you want to see" },
    ],
    popularRoutes: ["athens-santorini","athens-mykonos"],
  },
  "charter-vip": {
    title: "VIP & Corporate",
    subtitle: "Executive travel across Greece",
    hero: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=900&q=80&auto=format&fit=crop",
    color: "#0D56A6",
    aircraft: ["aw109","h145"],
    intro: "For executives and private travellers who measure time differently, helicopter charter is simply the rational choice. A meeting in Athens at noon, a site visit in Thessaloniki, a private dinner in Mykonos -- all in a single day, without the compromises of commercial aviation. Our twin-engine aircraft are configured for business travel: quiet cabins, comfortable seating, and the discretion that senior figures require.",
    highlights: [
      { icon: "seat", label: "Executive configuration", desc: "Twin-engine cabins for business travel" },
      { icon: "handshake", label: "Full discretion", desc: "Non-disclosure available on request" },
      { icon: "phone", label: "Concierge service", desc: "24h team for last-minute changes" },
      { icon: "globe", label: "Any destination", desc: "300+ landing zones across Greece" },
    ],
    popularRoutes: ["athens-mykonos","athens-santorini","athens-rhodes"],
  },
  "charter-airport": {
    title: "Airport Connections",
    subtitle: "Athens airport to any island in under an hour",
    hero: "https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=900&q=80&auto=format&fit=crop",
    color: "#0D56A6",
    aircraft: ["r66","h125","aw109","h145"],
    intro: "Athens International Airport Eleftherios Venizelos sits 27 km east of the city centre -- and from there, by helicopter, you can reach any of the major Cycladic islands in under an hour. For travellers arriving on long-haul flights who want to continue directly to their island destination without an overnight in Athens, our airport transfer service removes every friction from the connection.",
    highlights: [
      { icon: "plane", label: "Direct from airport", desc: "Helipad 8 min from terminal" },
      { icon: "greet", label: "Meet & greet", desc: "Our team meets you at arrivals" },
      { icon: "luggage", label: "Luggage handling", desc: "Direct transfer, no carousel queues" },
      { icon: "stopwatch", label: "Athens to island", desc: "Under 60 minutes to most destinations" },
    ],
    popularRoutes: ["athens-mykonos","athens-santorini","athens-paros","athens-milos"],
  },
  "charter-custom": {
    title: "Custom Itineraries",
    subtitle: "Multi-stop, proposals, weddings, events — tell us the day, we plan the flight",
    hero: "https://images.unsplash.com/photo-1597466599360-3b9775841aec?w=800&q=80&auto=format&fit=crop",
    color: "#0D56A6",
    aircraft: ["r66","h125","aw109","h145"],
    intro: "Some flights aren't routine transfers — they're the centrepiece of an occasion. A surprise proposal over Santorini's caldera at golden hour. A multi-stop wedding day that takes guests from ceremony to reception to villa across three islands. A film crew shooting aerial sequences over the Cyclades. A corporate roadshow visiting six destinations in two days. We design each itinerary one at a time — choosing the aircraft, plotting the route, coordinating helipads and ground transfers, then quoting a single fixed price covering everything. Tell us what you're trying to do; our team will come back with an arrangement that works.",
    highlights: [
      { icon: "mappin", label: "Multi-stop routing", desc: "Three, five, ten stops across the Cyclades or beyond — designed around your day" },
      { icon: "gift", label: "Special occasions", desc: "Proposals, weddings, milestone celebrations with full coordination" },
      { icon: "camera", label: "Photo & film", desc: "Doors-off aerial photography, drone-style sequences, hovering positions" },
      { icon: "handshake", label: "Corporate & events", desc: "Roadshows, off-sites, executive transfers with multi-day coordination" },
    ],
    popularRoutes: ["athens-mykonos","athens-santorini","mykonos-santorini","athens-paros"],
  },
};
